import React from 'react';
import { Dialog, DialogContent, Box, Typography, Button, CircularProgress } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { layout, radius } from '../../themes/smartinventoryTheme';

// One component for all seven destructive confirms: tinted icon square,
// count-or-record-name in the title, the affected records listed, a
// consequence line, red primary on the right. `records` is an optional list
// of already-translated/display strings (product names, etc.) to enumerate.
//
// `children`, if given, renders between the records list and the footer —
// the escape hatch for the handful of confirms that also carry a small form
// (e.g. bulk category change: "pick the new category" + the affected list).
// Prefer leaving it out; reach for it before writing a second confirm shape.
const ConfirmDialog = ({
  open,
  onClose,
  tone = 'danger',
  icon: Icon = WarningAmberIcon,
  title,
  description,
  records,
  cancelLabel,
  confirmLabel,
  onConfirm,
  loading,
  confirmDisabled,
  children,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogContent>
      <Box
        sx={{
          width: layout.stateIconSize * 0.7,
          height: layout.stateIconSize * 0.7,
          borderRadius: `${radius.md}px`,
          bgcolor: tone === 'danger' ? 'error.light' : 'primary.light',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        <Icon color={tone === 'danger' ? 'error' : 'primary'} />
      </Box>
      <Typography variant="h5">{title}</Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {description}
        </Typography>
      )}
      {records && records.length > 0 && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: `${radius.md}px`, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
          {records.map((record) => (
            <Typography key={record} variant="body2">{record}</Typography>
          ))}
        </Box>
      )}
      {children}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mt: 2.5 }}>
        <Button variant="text" onClick={onClose} disabled={loading}>{cancelLabel}</Button>
        <Button
          variant="contained"
          color={tone === 'danger' ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={loading || confirmDisabled}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
        </Button>
      </Box>
    </DialogContent>
  </Dialog>
);

export default ConfirmDialog;
